// Probate Finance Ireland — probate bridging valuation form.
//
// A residential valuation at its core with two gating questions (primary use,
// farming entity) that open the Holiday Home and Agricultural Land sections.
// Branch panels are always present in the stepper (hiding whole steps would
// corrupt completedSteps) and show a single "Not applicable" line when closed.
//
// `visibleWhen` rules are evaluated by helpers/fieldVisibility; a hidden field
// is never required, never prints, and its answer is dropped on save.
//
// Lender name is injected once via createPfiForm so the coming rebrand is a
// bankConfig change, not a find-and-replace.

const META = '{"className":"mb-4", "isMediumFont":true}';
const META_COMPUTED = '{"className":"mb-4", "isMediumFont":true, "computed":true}';

const required = (msg = "this field is required") => ({
  validationType: "STRING",
  parameters: [{ type: "required", value: [msg] }]
});
const optional = () => ({ validationType: "STRING", parameters: [] });

// Visibility condition — see helpers/fieldVisibility. A field with
// `visibleWhen` plus `required()` is "required whenever shown".
const when = (fieldUI, value) => ({
  field: fieldUI,
  oneOf: Array.isArray(value) ? value : [value]
});

// Residential element + agricultural land must reconcile with Market Value.
// Declared on both operands so whichever is edited last re-validates.
const APPORTIONMENT = {
  type: "sum",
  operands: ["pfi-value-dwelling", "pfi-value-land"],
  equals: "market-value",
  value: ["Residential element + agricultural land must equal the Market Value"]
};
const requiredSum = () => ({
  validationType: "STRING",
  parameters: [{ type: "required", value: ["this field is required"] }, APPORTIONMENT]
});

const field = (fieldType, fieldUI, label, extra = {}) => ({
  fieldType,
  fieldUI,
  label,
  meta: META,
  validation: optional(),
  ...extra
});
const radio = (fieldUI, label, options, extra = {}) =>
  field("RADIO_GROUP", fieldUI, label, { options, ...extra });
const input = (fieldUI, label, extra = {}) => field("INPUT", fieldUI, label, extra);
const price = (fieldUI, label, extra = {}) =>
  input(fieldUI, label, { fieldSubType: "price", placeholder: "€", ...extra });
const number = (fieldUI, label, extra = {}) => field("NUMBER", fieldUI, label, extra);
const textarea = (fieldUI, label, extra = {}) => field("TEXT_AREA", fieldUI, label, extra);
const date = (fieldUI, label, extra = {}) => field("DATE_PICKER", fieldUI, label, extra);
const image = (fieldUI, name, extra = {}) => ({
  fieldType: "IMAGE",
  fieldUI,
  name,
  meta: '{"className":"mt-4 mr-4 mb-4", "isMediumFont":true}',
  subPanel: "images",
  validation: optional(),
  ...extra
});
const text = (html, extra = {}) => ({ fieldType: "TEXT", html, meta: META, validation: optional(), ...extra });
// Read-only line derived by FormCalculations (see calculationRules.js).
const derived = (fieldUI, label) => input(fieldUI, label, { meta: META_COMPUTED });

const YES_NO = ["Yes", "No"];
const YES_NO_UNKNOWN = ["Yes", "No", "Unknown"];
const FARMING = [when("pfi-farming-entity", "Yes")];
const HOLIDAY = [when("pfi-primary-use", "Holiday home")];
const VACANT = [when("pfi-occupancy-status", "Vacant")];

const notesPanel = legalEntityName => ({
  name: "Notes and recommendations",
  panelBreak: 1,
  fields: [
    text(
      `<div> <span class='text-accent-blue'>NOTES AND RECOMMENDATIONS:</span>
      This document is a report from the Lender’s Valuer to ${legalEntityName} (the Lender), providing an opinion of the Market Value and the condition of the property <strong>FOR THE PURPOSES OF A PROBATE BRIDGING FACILITY ONLY.</strong>
      This report is made for the sole benefit of the Lender, it remains the property of the Lender and cannot be relied upon by the personal representative(s), the beneficiaries of the estate or any other party.<br/>
      It is stressed that this report is based on a limited inspection carried out for lending purposes only. It is <strong>NOT A BUILDING OR STRUCTURAL SURVEY.</strong> There may be defects in the property the existence or extent of which could only be revealed by a detailed inspection. Where the property is unoccupied, its condition may deteriorate between the inspection and any subsequent sale.</div>`
    ),
    text(
      `<div> <strong>YOU SHOULD NOT ASSUME</strong> that the defects shown (if any) are the only ones present in the property, or if no repairs are mentioned, that no defects exist. No responsibility whatsoever is implied or accepted or warranty given by the Lender for the value or condition of the property by reason of this valuation.<br/>
      The estate is strongly recommended to arrange an independent valuation and, before any sale or retention decision, a complete Building Survey (including a survey of the structure) by a Chartered/Registered Building Surveyor, Registered Architect or Engineer. Specialist reports (e.g. Pyrite, ‘Dry Rot’ or ‘Subsidence’) may also be appropriate.</div>`
    )
  ]
});

const summaryPanel = {
  name: "Summary",
  panelBreak: 4,
  fields: [
    // Kept fieldUIs: getValuationRequestValues pre-fills these from the valuation.
    input("mortgage-ref-number", "PFI loan reference", {
      validation: {
        validationType: "STRING",
        parameters: [
          { type: "required", value: ["this field is required"] },
          { type: "min", value: [5, "reference cannot be less than 5 characters"] },
          { type: "max", value: [16, "reference cannot be more than 16 characters"] }
        ]
      }
    }),
    textarea("name-of-applicant", "Estate of (name of deceased)", { validation: required() }),
    textarea("pfi-personal-representative", "Personal representative(s) / executor(s)", { validation: required() }),
    input("pfi-solicitor", "Acting solicitor (firm and contact)"),
    price("market-value", "Market value", { validation: required() }),
    radio("valuation-type", "Valuation type", ["Full", "Final", "Subsequent"], {
      defaultValue: "Full",
      validation: required()
    }),
    date("valuation-date", "Valuation date", { validation: required() }),
    radio(
      "pfi-intended-outcome",
      "Intended outcome for the property",
      ["Retain by beneficiary", "Sale on open market", "Undecided"],
      { defaultValue: "Undecided", validation: required() }
    ),
    input("sales-agent", "Sales agent", {
      visibleWhen: [when("pfi-intended-outcome", "Sale on open market")],
      validation: required()
    })
  ]
};

const locationPanel = {
  name: "Location",
  panelBreak: 3,
  fieldsGroups: [
    {
      fieldGroupUI: "post",
      meta: '{"className": "flex justify-between mb-4"}'
    },
    {
      fieldGroupUI: "county",
      meta: '{"className": "flex justify-between mb-4"}'
    }
  ],
  fields: [
    {
      fieldType: "TEXT_AREA",
      html: null,
      fieldUI: "address",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Address",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "0post-code",
      options: null,
      placeholder: null,
      group: "post",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Post code (if Dublin)",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      NAOption: true,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "eir-code",
      options: null,
      placeholder: null,
      group: "post",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Eircode",
      meta: '{"className":"mb-4 ml-4","isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      NAOption: true,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "county",
      options: null,
      placeholder: null,
      group: "county",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "County",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "type",
      options: ["Urban", "Rural", "Suburban"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: "Urban",
      label: "Location Type",
      meta: '{"className":"mb-4", "isMediumFont":true,  "fullWidth": true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "access-to-public-road",
      options: ["Yes", "No"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: 0,
      defaultValue: null,
      label:
        "Does the property have access to or frontage onto a public road?",
      meta: '{"className":"mb-4", "isMediumFont":true, "marginRight": 4}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT_AREA",
      html: null,
      fieldUI: "additional-information",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Additional Location Comments (if relevant)",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: []
      }
    }
  ],
  subPanels: null
};

const propertyUsePanel = {
  name: "Property Use & Eligibility",
  panelBreak: 3,
  fields: [
    text("<div><span class='text-accent-blue'>ELIGIBILITY</span>: These answers decide which additional sections open and feed the Lender Eligibility Summary at the end of the report.</div>"),
    radio(
      "pfi-primary-use",
      "Primary use",
      ["Owner-occupied residential", "Residential investment", "Holiday home"],
      { defaultValue: "Owner-occupied residential", validation: required() }
    ),
    radio(
      "pfi-farming-entity",
      "Does this property form part of a farming entity or include agricultural land?",
      YES_NO,
      { defaultValue: "No", validation: required() }
    ),
    radio("pfi-under-two-years", "Was construction completed within the last 2 years?", YES_NO, {
      defaultValue: "No",
      validation: required()
    }),
    radio(
      "pfi-construction-type",
      "Construction type",
      ["Traditional masonry", "Timber frame", "Steel frame", "Other non-traditional", "Unknown"],
      { defaultValue: "Traditional masonry", validation: required() }
    ),
    radio(
      "pfi-construction-certified",
      "Is the construction system certified (NSAI Agrément / IAB) with completion and compliance certificates available?",
      YES_NO_UNKNOWN,
      {
        visibleWhen: [when("pfi-construction-type", ["Timber frame", "Steel frame", "Other non-traditional"])],
        validation: required()
      }
    ),
    textarea("pfi-certification-details", "Certification details (certifying body, certificate references, completion certificate)", {
      visibleWhen: [when("pfi-construction-type", ["Timber frame", "Steel frame", "Other non-traditional"])]
    })
  ]
};

const descriptionPanel = {
  name: "Description",
  panelBreak: 5,
  subPanels: [
    {
      subPanelUI: "property_type",
      meta: "",
      subPanelBreak: null
    },
    {
      subPanelUI: "exterior",
      meta: '{"formLayoutClassName": "flex justify-between"}',
      subPanelBreak: 0
    },
    {
      subPanelUI: "condition",
      meta: '{"formLayoutClassName": "flex justify-between"}',
      subPanelBreak: 0
    },
    {
      subPanelUI: "complete_status",
      meta: '{"formLayoutClassName": "flex justify-between"}',
      subPanelBreak: 0
    },
    {
      subPanelUI: "energy",
      meta: "",
      subPanelBreak: 2
    },
    {
      subPanelUI: "additional",
      meta: "",
      subPanelBreak: 5
    }
  ],
  fieldsGroups: [
    {
      fieldGroupUI: "service",
      meta: '{"className": "flex justify-between mb-4 items-end", "wrapGroupInPDF": true}'
    },
    {
      fieldGroupUI: "area",
      meta: '{"className": "flex justify-between mb-4"}'
    },
    {
      fieldGroupUI: "completion",
      meta: '{"className": "flex flex-wrap justify-start mb-4 ml-8", "wrapGroupInPDF": true}'
    }
  ],
  fields: [
    {
      fieldType: "TEXT",
      html: "<strong>Property Type</strong>",
      fieldUI: null,
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: null,
      icon: null,
      subPanel: "property_type",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "property-type-a",
      options: ["New build", "Second hand"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "a)",
      meta: '{"className":"mb-4", "isMediumFont":true,  "marginRight": 4}',
      icon: null,
      subPanel: "property_type",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "property-type-b",
      options: [
        "Detached",
        "Semi-detached",
        "Mid-terrace",
        "End of terrace",
        "Cottage",
        "Duplex",
        "Apartment",
        "Penthouse",
        "Bungalow",
        "Dormer",
        "Other"
      ],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "b)",
      meta: '{"className":"mb-4",   "marginBottom": 4,  "flexStart": true}',
      icon: null,
      subPanel: "property_type",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      fieldSubType: "price",
      html: null,
      fieldUI: "annual-service-charge",
      options: null,
      placeholder: "€",
      group: "service",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Annual Service Charge (if applicable)",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "property_type",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "property-type-b",
            conditionValue: [
              "Detached",
              "Semi-detached",
              "Mid-terrace",
              "End of terrace",
              "Cottage",
              "Duplex",
              "Apartment",
              "Penthouse",
              "Bungalow",
              "Dormer",
              "Other"
            ],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "NUMBER",
      html: null,
      fieldUI: "number-of-bedrooms",
      options: null,
      placeholder: null,
      group: "service",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Number of Bedrooms",
      meta: '{"className":"mb-4  ml-4", "isMediumFont":true}',
      icon: null,
      subPanel: "property_type",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "NUMBER",
      html: null,
      fieldUI: "floor-area",
      options: null,
      placeholder: null,
      group: "area",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Floor Area (m2)",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "property_type",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "cost-per-sqm",
      options: null,
      placeholder: null,
      group: "area",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Cost per m²",
      meta: '{"className":"mb-4", "isMediumFont":true, "computed":true}',
      icon: null,
      subPanel: "property_type",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: []
      }
    },
    {
      fieldType: "NUMBER",
      html: null,
      fieldUI: "plot-size",
      options: null,
      placeholder: null,
      group: "area",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Plot Size (Hectares)",
      meta: '{"className":"mb-4  ml-4", "isMediumFont":true}',
      icon: null,
      subPanel: "property_type",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: []
      }
    },
    {
      fieldType: "TEXT",
      html: "<strong>If apartment:</strong>",
      fieldUI: null,
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: null,
      icon: null,
      subPanel: "property_type",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "NUMBER",
      html: null,
      fieldUI: "no-of-floors-in-block",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "a) No of Floors in Block",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "property_type",
      name: null,
      NAOption: true,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "NUMBER",
      html: null,
      fieldUI: "apartment-floor",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "b) Apartment Floor (Location)",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "property_type",
      name: null,
      NAOption: true,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "balconies-terrace-area",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "c) Balconies/Terrace area",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "property_type",
      name: null,
      NAOption: true,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "any-extensions",
      options: ["Yes", "No"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Any extensions:",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "property_type",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: []
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "any-outbuildings",
      options: ["Yes", "No"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Any outbuildings:",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "property_type",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: []
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "garden",
      options: ["Front", "Back", "Both"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Garden",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "exterior",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: []
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "property-type",
      options: ["Drive-way", "Garage", "On street", "Communal"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Car parking",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "exterior",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: []
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "how-many-car",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "How many car spaces included in valuation:",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "exterior",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: []
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "approximate-year-of-completion",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Approximate year of completion",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "condition",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "external-condition",
      options: ["Excellent", "Good", "Fair", "Poor"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "External condition",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "condition",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "internal-condition",
      options: ["Excellent", "Good", "Fair", "Poor"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Internal condition",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "condition",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "is-the-property-complete",
      options: ["Yes", "No"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Is the property complete? (kitchen, bathroom, etc, ...)",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "complete_status",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT",
      html: "<strong>If 'No':</strong>",
      fieldUI: null,
      options: null,
      placeholder: null,
      group: "completion",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: '{"className":"w-full"}',
      icon: null,
      subPanel: "complete_status",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "DATE_PICKER",
      html: null,
      fieldUI: "estimated-completion-date",
      options: null,
      placeholder: "",
      group: "completion",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "a) Estimated completion date",
      meta: '{"className":"mb-4 mr-4", "isMediumFont":true}',
      icon: null,
      subPanel: "complete_status",
      name: null,
      NAOption: true,
      unsetDefaultValue: true,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "is-the-property-complete",
            conditionValue: ["No"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      fieldSubType: "price",
      html: null,
      fieldUI: "value-in-current-condition",
      options: null,
      placeholder: "€",
      group: "completion",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "b) Value in current condition",
      meta: '{"className":"mb-4 mr-4", "isMediumFont":true}',
      icon: null,
      subPanel: "complete_status",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "is-the-property-complete",
            conditionValue: ["No"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      fieldSubType: "price",
      html: null,
      fieldUI: "value-on-completion",
      options: null,
      placeholder: "€",
      group: "completion",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "c) Value on completion",
      meta: '{"className":"mb-4 mr-4", "isMediumFont":true}',
      icon: null,
      subPanel: "complete_status",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "is-the-property-complete",
            conditionValue: ["No"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "central-heating-system",
      options: ["Oil", "Gas", "Solid Fuel", "Heat Pump", "Air Recovery System", "Other"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Central Heating System",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "energy",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "sewage-system",
      options: ["Mains", "Private Septic Tank"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Sewage System",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "energy",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "building-energy-rating",
      options: ["Yes", "No", "Not applicable"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Building Energy Rating",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "energy",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "if-yes-rating",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "If 'Yes', rating:",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "energy",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "building-energy-rating",
            conditionValue: ["Yes"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "any-visual",
      options: ["Yes", "No"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label:
        "Any visual evidence of Dry Rot, Subsisdence, Woodworm, Settlement or anything else negatively impacting value",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "additional",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "is-the-property-prone-to-flooding",
      options: ["Yes", "No"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Is the property prone to flooding?",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "additional",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "is-the-property-free-from-visual",
      options: ["Yes", "No"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Is the property free from visual signs of pyrite?",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "additional",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "is-a-building-surveyor",
      options: ["Yes", "No"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Is a Building Surveyor required?",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "additional",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "do-you-believe",
      options: ["Yes", "No"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label:
        "Do you believe any other specialist reports are required?",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "additional",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      fieldSubType: "price",
      html: null,
      fieldUI: "estimated-reinstatement-value",
      options: null,
      placeholder: "€",
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Estimated Reinstatement Value",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "additional",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "is-the-property-used-wholly",
      options: ["Yes", "No"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label:
        "Is the property to be used wholly as a private residence?",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "additional",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT_AREA",
      html: null,
      fieldUI: "additional-description-comments",
      options: null,
      placeholder: "€",
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Additional Description Comments (if relevant)",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "additional",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: []
      }
    }
  ]
};

const occupancyPanel = {
  name: "Occupancy & Insurance",
  panelBreak: 4,
  fields: [
    radio(
      "pfi-occupancy-status",
      "Occupancy status",
      ["Vacant", "Occupied by family member", "Occupied by tenant", "Other"],
      { defaultValue: "Vacant", validation: required() }
    ),
    textarea("pfi-occupant-details", "Who is in occupation and on what basis (licence, tenancy, informal)?", {
      visibleWhen: [when("pfi-occupancy-status", ["Occupied by family member", "Occupied by tenant", "Other"])],
      validation: required()
    }),
    input("pfi-vacant-duration", "Approximately how long has the property been vacant?", { visibleWhen: VACANT }),
    radio("pfi-vacant-secured", "Is the property secured (locks, alarm, boarded)?", YES_NO, {
      visibleWhen: VACANT,
      validation: required()
    }),
    radio("pfi-vacant-deterioration", "Signs of deterioration or vandalism", ["None", "Minor", "Significant"], {
      visibleWhen: VACANT
    }),
    radio("pfi-utilities-connected", "Utilities", ["Connected", "Disconnected", "Unknown"]),
    radio("pfi-insurance-in-place", "Is buildings insurance in place?", YES_NO_UNKNOWN, { validation: required() }),
    radio("pfi-insurance-vacant-cover", "Does the policy include unoccupied-property cover?", YES_NO_UNKNOWN, {
      visibleWhen: [when("pfi-insurance-in-place", "Yes")]
    }),
    input("pfi-insurance-details", "Insurer / policy reference (if known)", {
      visibleWhen: [when("pfi-insurance-in-place", "Yes")]
    })
  ]
};

const agriculturalLandPanel = {
  name: "Agricultural Land",
  panelBreak: 9,
  fieldsGroups: [{ fieldGroupUI: "pfi-apportionment", meta: '{"wrapGroupInPDF": true}' }],
  fields: [
    text("<div>Not applicable — the property does not form part of a farming entity.</div>", {
      visibleWhen: [when("pfi-farming-entity", "No")]
    }),
    number("pfi-land-area", "Total agricultural land area (hectares)", { visibleWhen: FARMING, validation: required() }),
    radio("pfi-land-use", "Land use", ["Grazing", "Tillage", "Forestry", "Mixed", "Other"], { visibleWhen: FARMING }),
    radio(
      "pfi-dwelling-separable",
      "Can the dwelling and its curtilage be separated from the farming entity?",
      ["Yes", "No", "With difficulty"],
      { visibleWhen: FARMING, validation: required() }
    ),
    textarea("pfi-separability-commentary", "Separability commentary (access, services, yard proximity, easements)", {
      visibleWhen: FARMING,
      validation: required()
    }),
    radio("pfi-own-road-access", "Does the dwelling have its own independent access onto a public road?", YES_NO, {
      visibleWhen: FARMING
    }),
    number("pfi-road-frontage", "Road frontage (metres)", { visibleWhen: FARMING }),
    radio("pfi-independent-services", "Are water, power and drainage/septic independent of the farm?", ["Yes", "No", "Partial"], {
      visibleWhen: FARMING
    }),
    radio("pfi-shared-laneway", "Are there shared laneways, rights of way or easements with the farm?", YES_NO, {
      visibleWhen: FARMING
    }),
    textarea("pfi-easement-details", "Easement / right of way details", {
      visibleWhen: [...FARMING, when("pfi-shared-laneway", "Yes")]
    }),
    number("pfi-yard-proximity", "Distance from dwelling to farmyard (metres)", { visibleWhen: FARMING }),
    radio("pfi-land-sheds", "Sheds or agricultural outbuildings on the land?", YES_NO, { visibleWhen: FARMING }),
    textarea("pfi-land-sheds-details", "Outbuilding details", {
      visibleWhen: [...FARMING, when("pfi-land-sheds", "Yes")]
    }),
    radio("pfi-land-flood", "Is the land liable to flooding?", YES_NO, { visibleWhen: FARMING }),
    radio("pfi-land-registered", "Title", ["Registered (folio)", "Unregistered", "Mixed"], { visibleWhen: FARMING }),
    input("pfi-folio-1", "Folio number 1", { visibleWhen: FARMING }),
    input("pfi-folio-2", "Folio number 2", { visibleWhen: FARMING }),
    input("pfi-folio-3", "Folio number 3", { visibleWhen: FARMING }),
    text("<strong>Apportionment of Market Value</strong>", { visibleWhen: FARMING, group: "pfi-apportionment" }),
    price("pfi-value-dwelling", "Value of dwelling and curtilage (residential element)", {
      visibleWhen: FARMING,
      group: "pfi-apportionment",
      validation: requiredSum()
    }),
    price("pfi-value-land", "Value of agricultural land", {
      visibleWhen: FARMING,
      group: "pfi-apportionment",
      validation: requiredSum()
    }),
    input("pfi-value-total-check", "Total of elements (must equal Market Value)", {
      visibleWhen: FARMING,
      group: "pfi-apportionment",
      meta: META_COMPUTED
    })
  ]
};

const holidayHomePanel = {
  name: "Holiday Home",
  panelBreak: 4,
  fields: [
    text("<div>Not applicable — the property is not a holiday home.</div>", {
      visibleWhen: [when("pfi-primary-use", ["Owner-occupied residential", "Residential investment"])]
    }),
    radio(
      "pfi-holiday-type",
      "Type",
      ["Standard dwelling used as a holiday home", "Purpose-built unit within a holiday scheme"],
      { visibleWhen: HOLIDAY, validation: required() }
    ),
    radio(
      "pfi-holiday-planning-restriction",
      "Is there a planning condition restricting occupancy (e.g. holiday use only)?",
      YES_NO_UNKNOWN,
      { visibleWhen: HOLIDAY, validation: required() }
    ),
    textarea("pfi-holiday-restriction-details", "Restriction details", {
      visibleWhen: [...HOLIDAY, when("pfi-holiday-planning-restriction", "Yes")]
    }),
    radio("pfi-holiday-management-company", "Is there a management company?", YES_NO, { visibleWhen: HOLIDAY }),
    price("pfi-holiday-annual-fees", "Annual management fees", {
      visibleWhen: [...HOLIDAY, when("pfi-holiday-management-company", "Yes")]
    }),
    radio("pfi-holiday-access", "Access", ["Year-round", "Seasonal / restricted"], { visibleWhen: HOLIDAY }),
    textarea("pfi-holiday-marketability", "Marketability and liquidity commentary", {
      visibleWhen: HOLIDAY,
      validation: required()
    })
  ]
};

const lettingPotentialPanel = {
  name: "Letting Potential",
  fieldsGroups: [
    {
      fieldGroupUI: "post",
      meta: '{"className": "flex justify-between mb-4"}'
    },
    {
      fieldGroupUI: "post",
      meta: '{"className": "flex justify-between mb-4"}'
    }
  ],
  panelBreak: 7,
  fields: [
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "property-use",
      options: ["Owner Occupied", "Investment"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Property Use",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "is-the-property-suitable",
      options: ["Yes", "No"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label:
        "Is the property suitable for letting in current condition?",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT_AREA",
      html: null,
      fieldUI: "letting-condition-details",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "If 'No', details:",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "is-the-property-suitable",
            conditionValue: ["No"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "level-of-demand",
      options: ["Good", "Fair", "Poor"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Level of demand in area",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "NUMBER",
      html: null,
      fieldUI: "estimated-time-to-let",
      options: null,
      placeholder: "months",
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Estimated time to let:",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "lease-in-place",
      options: ["yes", "no"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Is there a lease in place?",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "property-use",
            conditionValue: ["Investment"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT_AREA",
      html: null,
      fieldUI: "lease-details",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "If 'Yes', details:",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "lease-in-place",
            conditionValue: ["yes"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "current-rent",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      NAOption: true,
      label: "Current Rent (per month) if rented",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "lease-in-place",
            conditionValue: ["yes"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "market-rent",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Potential Market Rent/Value (per month) even if not rented",
      NAOption: false,
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "asset-located",
      options: ["Yes", "No"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Is the asset located in a rent pressure zone?",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "DATE_PICKER",
      html: null,
      fieldUI: "next-review-date",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "If Yes, date of next review DD/MM/YYYY",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      NAOption: true,
      unsetDefaultValue: true,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "asset-located",
            conditionValue: ["Yes"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      fieldSubType: "price",
      html: null,
      fieldUI: "next-review",
      options: null,
      placeholder: "€",
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      NAOption: true,
      label: "Rent at next review",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "asset-located",
            conditionValue: ["Yes"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT_AREA",
      html: null,
      fieldUI: "additional-valuer-comments",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Additional Valuer Comments (if relevant)",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: []
      }
    }
  ],
  subPanels: null
};

const valuationRationalePanel = {
  name: "Valuation Rationale",
  fieldsGroups: [
    {
      fieldGroupUI: "post",
      meta: '{"className": "flex justify-between mb-4"}'
    },
    {
      fieldGroupUI: "post",
      meta: '{"className": "flex justify-between mb-4"}'
    }
  ],
  subPanels: [
    {
      subPanelUI: "market",
      meta: null,
      subPanelBreak: 2
    }
  ],
  panelBreak: 3,
  fields: [
    {
      fieldType: "INPUT",
      fieldSubType: "price",
      html: null,
      fieldUI: "market-value",
      options: null,
      placeholder: "€",
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Market Value",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "market",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "DATE_PICKER",
      html: null,
      fieldUI: "valuation-date",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Valuation Date DD/MM/YYYY",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "market",
      name: null,
      unsetDefaultValue: true,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "is-suitable-for-mortgage",
      options: ["Yes", "No"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label:
        "Is the property in its current state suitable for a mortgage?",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "market",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT_AREA",
      html: null,
      fieldUI: "if-no",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "If 'No', details:",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "market",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "is-suitable-for-mortgage",
            conditionValue: ["No"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "could-property-be-disposed",
      options: ["Yes", "No"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label:
        "Could the property be readily disposed of at your valuation?",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT_AREA",
      html: null,
      fieldUI: "disposed-details",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "If 'No', details:",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "could-property-be-disposed",
            conditionValue: ["No"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "estimated-time",
      options: null,
      placeholder: "months",
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Estimated time to sell on the open market",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "future-value",
      options: ["Yes", "No"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label:
        "Are there any factors in the short term that would have an adverse effect on future value?",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT_AREA",
      html: null,
      fieldUI: "future-value-details",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "If 'Yes', details:",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: null,
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "future-value",
            conditionValue: ["Yes"],
            value: ["this field is required"]
          }
        ]
      }
    },
    // Probate-specific bases of value. All optional and N/A-able; PFI confirm
    // whether they are instructed as standard.
    date("pfi-date-of-death", "Date of death", { NAOption: true, unsetDefaultValue: true }),
    price("pfi-date-of-death-value", "Market value at date of death (if instructed)", { NAOption: true }),
    price("pfi-value-180-day", "Value assuming a 180-day marketing period", { NAOption: true })
  ]
};

const valuationCommentaryPanel = {
  name: "Valuation Commentary",
  fieldsGroups: [
    {
      fieldGroupUI: "price_m2",
      meta: '{"className": "flex justify-between mb-4"}'
    }
  ],
  subPanels: [
    {
      subPanelUI: "assumptions",
      meta: '{"formLayoutClassName": "w-full flex"}',
      subPanelBreak: 0
    },
    {
      subPanelUI: "comparable-1",
      meta: '{"className": "flex justify-between mb-4"}',
      subPanelBreak: 5
    },
    {
      subPanelUI: "comparable-2",
      meta: '{"className": "flex justify-between mb-4"}',
      subPanelBreak: 5
    },
    {
      subPanelUI: "comparable-3",
      meta: '{"className": "flex justify-between mb-4"}',
      subPanelBreak: 5
    }
  ],
  panelBreak: null,
  fields: [
    {
      fieldType: "TEXT",
      html: "<div><span class='text-accent-blue'>NOTE:</span> Please detail any assumptions, special assumptions, reservations, special instructions or departures from Valuation standards.</div>",
      fieldUI: null,
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: '{"className": "w-full"}',
      icon: null,
      subPanel: "assumptions",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: []
      }
    },
    {
      fieldType: "TEXT_AREA",
      html: null,
      fieldUI: "valuation-commentary",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "",
      meta: '{"className":"mb-4 w-full"}',
      icon: null,
      subPanel: "assumptions",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: []
      }
    },
    {
      fieldType: "TEXT",
      html: "<span class='text-accent-blue'>Comparable 1</span>",
      fieldUI: null,
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: '{"className":"mb-4"}',
      icon: null,
      subPanel: "comparable-1",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "SELECT",
      html: null,
      fieldUI: "comparable-1-property-type",
      options: [
        "Detached",
        "Semi Detached",
        "Mid-Terrace",
        "End of Terrace",
        "Cottage",
        "Duplex",
        "Apartment",
        "Penthouse",
        "Bungalow",
        "Dormer",
        "Other"
      ],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Property Type",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-1",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "comparable-1-address",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Address",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-1",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "comparable-1-sales-price",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Sales Price",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-1",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "comparable-1-source",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Source",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-1",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "comparable-1-date-of-transaction",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Date of Transaction DD/MM/YYYY",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-1",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "comparable-1-m2",
      options: null,
      placeholder: "E",
      group: "price_m2",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "m2",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-1",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      fieldSubType: "price",
      html: null,
      fieldUI: "comparable-1-eur-m2",
      options: null,
      placeholder: null,
      group: "price_m2",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "€ per m2",
      meta: '{"className":"mb-4 ml-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-1",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "SELECT",
      html: null,
      fieldUI: "comparable-1-ber",
      options: [
        "N/A",
        "A1",
        "A2",
        "A3",
        "B1",
        "B2",
        "B3",
        "C1",
        "C2",
        "C3",
        "D1",
        "D2",
        "E1",
        "E2",
        "F",
        "G"
      ],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Building Energy Rating",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-1",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT_AREA",
      html: null,
      fieldUI: "comparable-1-subject-property",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Comparision to subject property",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-1",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT",
      html: "<span class='text-accent-blue'>Comparable 2</span>",
      fieldUI: null,
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: '{"className":"mb-4"}',
      icon: null,
      subPanel: "comparable-2",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "SELECT",
      html: null,
      fieldUI: "comparable-2-property-type",
      options: [
        "Detached",
        "Semi Detached",
        "Mid-Terrace",
        "End of Terrace",
        "Cottage",
        "Duplex",
        "Apartment",
        "Penthouse",
        "Bungalow",
        "Dormer",
        "Other"
      ],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Property Type",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-2",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "comparable-2-address",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Address",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-2",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "comparable-2-sales-price",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Sales Price",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-2",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "comparable-2-source",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Source",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-2",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "comparable-2-date-of-transaction",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Date of Transaction DD/MM/YYYY",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-2",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "comparable-2-m2",
      options: null,
      placeholder: "E",
      group: "price_m2",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "m2",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-2",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      fieldSubType: "price",
      html: null,
      fieldUI: "comparable-2-eur-m2",
      options: null,
      placeholder: null,
      group: "price_m2",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "€ per m2",
      meta: '{"className":"mb-4 ml-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-2",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "SELECT",
      html: null,
      fieldUI: "comparable-2-ber",
      options: [
        "N/A",
        "A1",
        "A2",
        "A3",
        "B1",
        "B2",
        "B3",
        "C1",
        "C2",
        "C3",
        "D1",
        "D2",
        "E1",
        "E2",
        "F",
        "G"
      ],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Building Energy Rating",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-2",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT_AREA",
      html: null,
      fieldUI: "comparable-2-subject-property",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Comparision to subject property",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-2",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT",
      html: "<span class='text-accent-blue'>Comparable 3</span>",
      fieldUI: null,
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: '{"className":"mb-4"}',
      icon: null,
      subPanel: "comparable-3",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "SELECT",
      html: null,
      fieldUI: "comparable-3-property-type",
      options: [
        "Detached",
        "Semi Detached",
        "Mid-Terrace",
        "End of Terrace",
        "Cottage",
        "Duplex",
        "Apartment",
        "Penthouse",
        "Bungalow",
        "Dormer",
        "Other"
      ],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Property Type",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-3",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "comparable-3-address",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Address",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-3",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "comparable-3-sales-price",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Sales Price",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-3",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "comparable-3-source",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Source",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-3",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "comparable-3-date-of-transaction",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Date of Transaction DD/MM/YYYY",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-3",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "comparable-3-m2",
      options: null,
      placeholder: "E",
      group: "price_m2",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "m2",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-3",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      fieldSubType: "price",
      html: null,
      fieldUI: "comparable-3-eur-m2",
      options: null,
      placeholder: null,
      group: "price_m2",
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "€ per m2",
      meta: '{"className":"mb-4 ml-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-3",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "SELECT",
      html: null,
      fieldUI: "comparable-3-ber",
      options: [
        "N/A",
        "A1",
        "A2",
        "A3",
        "B1",
        "B2",
        "B3",
        "C1",
        "C2",
        "C3",
        "D1",
        "D2",
        "E1",
        "E2",
        "F",
        "G"
      ],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Building Energy Rating",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-3",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT_AREA",
      html: null,
      fieldUI: "comparable-3-subject-property",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Comparision to subject property",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comparable-3",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    }
  ]
};

const eligibilitySummaryPanel = {
  name: "Lender Eligibility Summary",
  panelBreak: 4,
  fields: [
    text("<div><span class='text-accent-blue'>SUMMARY</span>: Derived automatically from the answers above so the lender's credit team sees every senior-funding criterion in one place. Add anything material in the note below.</div>"),
    derived("pfi-elig-agri", "Agricultural land included"),
    derived("pfi-elig-new-build", "Construction completed within the last 2 years"),
    derived("pfi-elig-construction", "Non-traditional construction"),
    derived("pfi-elig-holiday-scheme", "Purpose-built holiday-scheme unit"),
    derived("pfi-elig-vacant", "Property vacant"),
    derived("pfi-elig-insured", "Buildings insurance confirmed"),
    textarea("pfi-lender-note", "Valuer's note to lender on eligibility")
  ]
};

const signOffPanel = legalEntityName => ({
  name: "Valuer Sign Off",
  // The PDF prints this panel's own fields (minus session/certificate) instead
  // of the Finance Ireland sign-off block in transformFormTemplate.
  pdfSignOff: "template",
  panelBreak: 4,
  subPanels: [
    {
      subPanelUI: "text",
      meta: null,
      subPanelBreak: 0
    },
    {
      subPanelUI: "fields",
      meta: null,
      subPanelBreak: 3
    },
    {
      subPanelUI: "comments",
      meta: null,
      subPanelBreak: 0
    },
    {
      subPanelUI: "session",
      meta: '{"formLayoutClassName": "", "formLayoutWrapperClassName": "mt-3 p-0" }',
      subPanelBreak: 0
    },
    {
      subPanelUI: "certificate",
      meta: '{"formLayoutClassName": "", "formLayoutWrapperClassName": "mt-3 p-0" }',
      subPanelBreak: 0
    },
  ],
  fields: [
    {
      fieldType: "TEXT",
      html: "<div><span class='text-accent-blue'>NOTE: </span> For the purpose of this valuation “Market Value” is defined as ‘the estimated amount for which an asset or liability should exchange on the valuation date between a willing buyer and a willing seller in an arm’s length transaction, after proper marketing and where the parties had each acted knowledgeably, prudently and without compulsion. I certify that the property described in this report has been inspected by me on the date of the inspection specified below, that I have the knowledge, skills, and understanding to undertake the valuation competently, that I have had full access to the property and that I have valued the property and prepared the report in accordance with International Valuation Standards and the RICS ‘Red Book’ or the TEGoVA ‘Blue Book’.</div><br>",
      fieldUI: null,
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: null,
      icon: null,
      subPanel: "text",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT",
      html: `<div>I certify that neither I, nor my employees, have a conflict of interest (as outlined in the terms of appointment to the lender’s valuation panel) in respect of this property. I certify that this firm holds up to date Professional Indemnity Insurance for a minimum of €1,000,000. A current photograph of the property is enclosed. This valuation report is addressed to the Lender, ${legalEntityName}, which proposes to provide the personal representative(s) of the above named estate with a probate bridging facility secured on the above property.</div>`,
      fieldUI: null,
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: null,
      icon: null,
      subPanel: "text",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "name-of-valuer",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Name of Valuer",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "fields",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "SIGNATURE",
      html: null,
      fieldUI: "signature-of-valuer",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Signature of Valuer",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "fields",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT_AREA",
      html: null,
      fieldUI: "company-name-address",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Company Name & Address",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "fields",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "RADIO_GROUP",
      html: null,
      fieldUI: "is-final-inspection-required",
      options: ["Yes", "No"],
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Is a Final Inspection & Report Required",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "fields",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "INPUT",
      html: null,
      fieldUI: "qualification-of-valuer",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Qualification of Valuer",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "fields",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "DATE_PICKER",
      html: null,
      fieldUI: "date-of-report",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Date of Report DD/MM/YYYY",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "fields",
      name: null,
      unsetDefaultValue: true,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "TEXT_AREA",
      html: null,
      fieldUI: "additional_location",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Additional Location Comments (if relevant)",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "comments",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: []
      }
    },
    {
      fieldType: "SESSION_INFO",
      html: null,
      fieldUI: "email-of-user",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Email of user",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "session",
      name: null,
    },
    {
      fieldType: "SESSION_INFO",
      html: null,
      fieldUI: "token-of-user",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Token of user",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "session",
      name: null,
    },
    {
      fieldType: "SESSION_INFO",
      html: null,
      fieldUI: "signing-date-of-report",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "Signing date",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "session",
      name: null,
    },
    {
      fieldType: "SESSION_INFO",
      html: null,
      fieldUI: "ip-of-user",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: "IP of user",
      meta: '{"className":"mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "session",
      name: null,
    },
    {
      fieldType: "CERTIFICATE",
      html: null,
      fieldUI: null,
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: null,
      icon: null,
      subPanel: "certificate",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
  ],
  groups: null,
});

const disclaimerPanel = legalEntityName => ({
  name: "Legal Disclaimer - Strictly Private & Confidential",
  panelBreak: 1,
  fields: [
    text(`<div>These materials have been provided to you by ${legalEntityName} for information purposes only. They should not be used or relied upon for any purpose. In addition, these materials may not be disclosed, in whole or in part, or summarized or otherwise referred to except as agreed in writing by ${legalEntityName}. The information used in preparing these materials was believed to be correct at the time of publication. ${legalEntityName} assumes no responsibility for independent verification of such information and has relied on such information being complete and accurate in all material respects. To the extent such information includes estimates and forecasts of future financial performance (including estimates of potential cost savings and synergies) prepared by or reviewed or discussed with the managements of your company and/or other potential transaction participants or obtained from public sources,</div>`),
    text(`<div>we have assumed that such estimates and forecasts have been reasonably prepared on bases reflecting the best currently available estimates and judgments of such managements (or, with respect to estimates and forecasts obtained from public sources, represent reasonable estimates). These materials were designed for use by specific persons familiar with the business and the affairs of your company and ${legalEntityName} assumes no obligation to update or otherwise revise these materials. Nothing contained herein should be construed as tax, accounting or legal advice.</div>`)
  ]
});

const photosPanel = {
  name: "Add Photos and Submit",
  subPanels: [
    {
      subPanelUI: "images",
      meta: '{"formLayoutClassName": "lg:grid lg:grid-cols-3"}',
      subPanelBreak: null
    }
  ],
  fields: [
    {
      fieldType: "TEXT",
      html: "<div><span class='text-accent-blue'>NOTE</span>: It is a requirement that photographs are provided for front, rear, kitchen, bathroom, bedroom(s) and street view. These must be submitted with each valuation. Please load your images to the photograph template below by clicking on the icons. Click <b>Save</b> to upload images and complete the form.</div>",
      fieldUI: null,
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: '{"className":"col-span-3", "isMediumFont":true}',
      icon: null,
      subPanel: "images",
      name: null,
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "required",
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "IMAGE",
      html: null,
      fieldUI: "front-elevation-image",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: '{"className":"mt-4 mr-4 mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "images",
      name: "Front Elevation *",
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "property-type-a",
            conditionValue: ["Second hand"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "IMAGE",
      html: null,
      fieldUI: "rear-elevation-image",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: '{"className":"mt-4 mr-4 mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "images",
      name: "Rear Elevation *",
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "property-type-a",
            conditionValue: ["Second hand"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "IMAGE",
      html: null,
      fieldUI: "kitchen-image",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: '{"className":"mt-4 mr-4 mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "images",
      name: "Kitchen *",
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "property-type-a",
            conditionValue: ["Second hand"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "IMAGE",
      html: null,
      fieldUI: "living-room-image",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: '{"className":"mt-4 mr-4 mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "images",
      name: "Living Room *",
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "property-type-a",
            conditionValue: ["Second hand"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "IMAGE",
      html: null,
      fieldUI: "main-bathroom-image",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: '{"className":"mt-4 mr-4 mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "images",
      name: "Main Bathroom *",
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "property-type-a",
            conditionValue: ["Second hand"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "IMAGE",
      html: null,
      fieldUI: "bedroom-1-image",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: '{"className":"mt-4 mr-4 mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "images",
      name: "Bedroom 1 *",
      validation: {
        validationType: "STRING",
        parameters: [
          {
            type: "conditional",
            conditionFieldUI: "property-type-a",
            conditionValue: ["Second hand"],
            value: ["this field is required"]
          }
        ]
      }
    },
    {
      fieldType: "IMAGE",
      html: null,
      fieldUI: "bedroom-2-image",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: '{"className":"mt-4 mr-4 mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "images",
      name: "Bedroom 2",
      validation: {
        validationType: "STRING",
        parameters: []
      }
    },
    {
      fieldType: "IMAGE",
      html: null,
      fieldUI: "other-image",
      options: null,
      placeholder: null,
      group: null,
      defaultCheckedIndex: null,
      defaultValue: null,
      label: null,
      meta: '{"className":"mt-4 mr-4 mb-4", "isMediumFont":true}',
      icon: null,
      subPanel: "images",
      name: "Other",
      validation: {
        validationType: "STRING",
        parameters: []
      }
    },
    image("pfi-land-image", "Land / yard", { visibleWhen: [when("pfi-farming-entity", "Yes")] }),
    image("pfi-access-image", "Access / laneway", { visibleWhen: [when("pfi-farming-entity", "Yes")] })
  ],
  fieldsGroups: null,
  panelBreak: null
};


export const PFI_LEGAL_ENTITY_NAME = "Probate Finance Ireland Ltd";

export const createPfiForm = ({ legalEntityName = PFI_LEGAL_ENTITY_NAME } = {}) => ({
  data: {
    valuationForm: {
      id: 1,
      content: '{"panel": "mawla-abc"}',
      active: 1,
      bankId: 1,
      panels: [
        notesPanel(legalEntityName),
        summaryPanel,
        locationPanel,
        propertyUsePanel,
        descriptionPanel,
        occupancyPanel,
        agriculturalLandPanel,
        holidayHomePanel,
        lettingPotentialPanel,
        valuationRationalePanel,
        valuationCommentaryPanel,
        eligibilitySummaryPanel,
        signOffPanel(legalEntityName),
        disclaimerPanel(legalEntityName),
        photosPanel
      ]
    }
  }
});

// Built with the default lender name; bank-service builds from bankConfig.
export const pfi_valuation_form_response = createPfiForm();
